/*
  SurfaceMaxx collection page refresh (templates/collection.refresh.json).
  Turned on by the SMX collection banner / chips sections through data attributes:
  - [data-smx-sort-unavailable]: move sold-out and coming-soon cards to the end
  - [data-smx-hide-coming-soon]: leave coming-soon cards out of the grid
  - [data-smx-collapse-filters]: start with every filter group closed
  - [data-smx-chip-filter]: chips filter the grid in place instead of opening a page
  The grid and the filter sidebar can be redrawn by the search app, so everything
  re-applies when they change.
*/
(() => {
  if (window.smxCollection) return;
  window.smxCollection = true;

  const grid = () => document.querySelector('#product-grid');
  const handleOf = (li) => {
    const a = li.querySelector('a[href*="/products/"]');
    if (!a) return null;
    const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  };

  /* ---------- Sold out and coming soon last ---------- */
  const isUnavailable = (li) =>
    !!li.querySelector('.coming-soon-badge, .oos-badge') ||
    !!li.querySelector('button[name="add"][disabled], .product-form__submit[disabled]');

  function sortUnavailable() {
    const g = grid();
    if (!g) return;
    const items = [...g.children];
    const late = items.filter(isUnavailable);
    // Only touch the DOM when an unavailable card sits before an available one
    const firstLate = items.findIndex(isUnavailable);
    const needs = firstLate !== -1 && items.slice(firstLate).some((li) => !isUnavailable(li));
    if (needs) late.forEach((li) => g.appendChild(li));
  }

  /* ---------- Hide coming soon ---------- */
  // Marked with an attribute (not `hidden`) so the chip filter can't show them again
  function hideComingSoon() {
    const g = grid();
    if (!g) return;
    [...g.children].forEach((li) => {
      if (li.hasAttribute('data-smx-coming-soon')) return;
      if (li.querySelector('.coming-soon-badge')) li.setAttribute('data-smx-coming-soon', '');
    });
  }

  /* ---------- Filters closed on load ---------- */
  const collapsed = new WeakSet();
  function collapseFilters() {
    document.querySelectorAll('.cloud-search-filter').forEach((f) => {
      if (collapsed.has(f)) return;
      collapsed.add(f);
      const open = f.querySelector('.cloud-search-filter__collapse');
      const name = f.querySelector('.cloud-search-filter__name');
      if (open && name) name.click();
    });
  }

  /* ---------- Chips filter the grid ---------- */
  const cache = new Map();
  let activeHandles = null;

  async function handlesFor(url) {
    if (cache.has(url)) return cache.get(url);
    const set = new Set();
    for (let page = 1; page <= 10; page++) {
      const res = await fetch(`${url}/products.json?limit=250&page=${page}`);
      if (!res.ok) break;
      const data = await res.json();
      data.products.forEach((p) => set.add(p.handle));
      if (data.products.length < 250) break;
    }
    cache.set(url, set);
    return set;
  }

  function applyChipFilter() {
    const g = grid();
    if (!g) return;
    let shown = 0;
    [...g.children].forEach((li) => {
      const h = handleOf(li);
      const show = !activeHandles || (h && activeHandles.has(h));
      li.hidden = !show;
      if (show && !li.hasAttribute('data-smx-coming-soon')) shown++;
    });
    const empty = document.querySelector('[data-smx-chip-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  function setupChips(nav) {
    const chips = [...nav.querySelectorAll('a.smx-chip')];
    const allUrl = nav.dataset.collectionUrl;
    const select = async (chip, push) => {
      const url = new URL(chip.href, location.origin).pathname;
      chips.forEach((c) => {
        const on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (url === allUrl) {
        activeHandles = null;
      } else {
        nav.setAttribute('aria-busy', 'true');
        try {
          activeHandles = await handlesFor(url);
        } catch (e) {
          location.href = chip.href; // fall back to opening the sub-collection
          return;
        } finally {
          nav.removeAttribute('aria-busy');
        }
      }
      applyChipFilter();
      if (push) {
        const u = new URL(location.href);
        if (url === allUrl) u.searchParams.delete('type');
        else u.searchParams.set('type', url.split('/').pop());
        history.replaceState(null, '', u);
      }
    };
    chips.forEach((chip) => {
      // Only collection links can filter; anything else stays a normal link
      if (!new URL(chip.href, location.origin).pathname.startsWith('/collections/')) return;
      chip.setAttribute('role', 'button');
      chip.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return; // let new-tab clicks through
        e.preventDefault();
        select(chip, true);
      });
    });
    const start = new URL(location.href).searchParams.get('type');
    if (start) {
      const chip = chips.find((c) => c.href.split('?')[0].endsWith(`/collections/${start}`));
      if (chip) select(chip, false);
    }
  }

  /* ---------- Wire up ---------- */
  function run() {
    const flags = document.querySelector('[data-smx-collection-flags]');
    const sort = flags && flags.hasAttribute('data-smx-sort-unavailable');
    const collapse = flags && flags.hasAttribute('data-smx-collapse-filters');
    const hideSoon = flags && flags.hasAttribute('data-smx-hide-coming-soon');
    const nav = document.querySelector('[data-smx-chip-filter]');
    if (nav) setupChips(nav);

    const apply = () => {
      if (hideSoon) hideComingSoon();
      if (sort) sortUnavailable();
      if (activeHandles) applyChipFilter();
      if (collapse) collapseFilters();
    };
    apply();
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        observer.disconnect();
        apply();
        observe();
      });
    });
    const observe = () => {
      const target = document.querySelector('#MainContent');
      if (target) observer.observe(target, { childList: true, subtree: true });
    };
    observe();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
